import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { Notify } from 'quasar';
import { useI18n } from 'vue-i18n';
import { useAuth } from 'src/boot/auth';
import { useSchoolFiltersStore } from 'src/stores/schoolFilters';
import {
  useSchoolData,
  type ClassCourseProgressRow,
  type ClassSectionProgressRow,
  type CourseOption,
  type SectionOption,
  type SchoolClassDetail,
} from 'src/modules/School/composables/useSchoolData';
import { useSchoolStudentStats } from 'src/modules/School/composables/useSchoolStudentStats';
import {
  assignableSectionOptions,
  assignableStudentOptions,
} from 'src/modules/School/utilities/assignmentOptions';
import {
  hasSemesterEnded,
  isCurrentSemester,
  isUpcomingSemester,
} from 'src/modules/School/utilities/semesterDates';

export type SchoolClassTabName = 'info' | 'students' | 'courses' | 'sections';
type AssignmentItemTab = 'courses' | 'sections';
/** The selected semester relative to today; null while none is selected. */
export type SemesterState = 'current' | 'future' | 'past' | null;

export function useSchoolClassDetailView() {
  const route = useRoute();
  const { currentUser } = useAuth();
  const schoolFiltersStore = useSchoolFiltersStore();
  const { t } = useI18n();
  const {
    fetchClassDetail,
    fetchClassAssignedCourses,
    fetchClassAssignedSections,
    fetchClassCourseProgress,
    fetchClassSectionProgress,
    setStudentCourseAssignment,
    bulkSetStudentCourseAssignment,
    setStudentSectionAssignment,
    bulkSetStudentSectionAssignment,
    decideEnrollment,
  } = useSchoolData();

  const activeTab = ref<SchoolClassTabName>('info');
  const classDetail = ref<SchoolClassDetail | null>(null);
  const assignedCourses = ref<CourseOption[]>([]);
  const totalAssignedCourses = ref(0);
  const assignedCoursesSearch = ref('');
  const isAssignedCoursesExpanded = ref(false);
  const selectedCourseDocumentId = ref('');
  const progressRows = ref<ClassCourseProgressRow[]>([]);
  const assignedSections = ref<SectionOption[]>([]);
  const totalAssignedSections = ref(0);
  const assignedSectionsSearch = ref('');
  const isAssignedSectionsExpanded = ref(false);
  const selectedSectionDocumentId = ref('');
  const sectionProgressRows = ref<ClassSectionProgressRow[]>([]);
  const isClassDataLoading = ref(false);
  const isProgressLoading = ref(false);
  const isSectionProgressLoading = ref(false);
  const isAssignmentDialogOpen = ref(false);
  const assignmentDialogTab = ref<AssignmentItemTab>('courses');
  const assignmentDialogStudentIds = ref<string[]>([]);
  const assignmentDialogCourseIds = ref<string[]>([]);
  const assignmentDialogSectionIds = ref<string[]>([]);
  const isApplyingAssignments = ref(false);

  // Per-student stats dialog (teacher view).
  const {
    summary: studentStatsSummary,
    loading: studentStatsLoading,
    error: studentStatsError,
    fetchStudentSummary,
  } = useSchoolStudentStats();
  const isStudentStatsDialogOpen = ref(false);
  const statsStudentName = ref('');

  function openStudentStats(studentDocumentId: string, studentName: string) {
    const classDocumentId = classDetail.value?.documentId;
    if (!classDocumentId || !studentDocumentId) return;
    statsStudentName.value = studentName;
    isStudentStatsDialogOpen.value = true;
    void fetchStudentSummary(
      classDocumentId,
      studentDocumentId,
      selectedSemester.value?.documentId,
    );
  }

  const canManageClassCourses = computed(() => {
    const roleType = currentUser.value?.role?.type;
    return (
      roleType === 'teacher' ||
      roleType === 'school_admin' ||
      roleType === 'admin'
    );
  });

  // Only teachers approve/decline student applications on their own classes.
  const canDecide = computed(() => currentUser.value?.role?.type === 'teacher');

  async function handleDecision(
    enrollmentDocumentId: string,
    decision: 'approve' | 'decline' | 'drop' | 'reinstate',
  ) {
    if (!enrollmentDocumentId) return;
    const updated = await decideEnrollment(enrollmentDocumentId, decision);
    if (!updated) {
      Notify.create({ message: t('ACTION_FAILED'), color: 'negative' });
      return;
    }
    Notify.create({ message: t('SCHOOL_DECISION_SAVED'), color: 'positive' });
    // The Courses/Sections student lists carry their own copy of each status
    // (it decides who is hidden as inactive), so reload them too.
    const [detail] = await Promise.all([
      fetchClassDetail(classDocumentId.value, semesterScope()),
      loadClassCourseProgress(),
      loadClassSectionProgress(),
    ]);
    classDetail.value = detail;
  }

  const classDocumentId = computed(() => {
    const documentId = route.params.documentId;
    if (typeof documentId === 'string') return documentId;
    if (Array.isArray(documentId)) return documentId[0] || '';
    return '';
  });

  const visibleTabs = computed<SchoolClassTabName[]>(() => {
    if (canManageClassCourses.value) {
      return ['info', 'students', 'courses', 'sections'];
    }
    return ['info', 'students'];
  });

  const selectedAssignedCourse = computed(() => {
    if (!selectedCourseDocumentId.value) return null;
    return (
      assignedCourses.value.find(
        (course) => course.documentId === selectedCourseDocumentId.value,
      ) || null
    );
  });

  const selectedAssignedSection = computed(() => {
    if (!selectedSectionDocumentId.value) return null;
    return (
      assignedSections.value.find(
        (section) => section.documentId === selectedSectionDocumentId.value,
      ) || null
    );
  });

  // Semester is the required context for viewing a class (school is optional and
  // no longer scopes a class). No semester → the class is not viewable.
  const hasSelectedSemester = computed(
    () => !!schoolFiltersStore.selectedSemesterDocumentId,
  );

  // Metadata (name/code/end_date) of the selected semester — for labelling and
  // gating the invite links (which must not appear for an ended semester).
  const selectedSemester = computed(
    () => schoolFiltersStore.selectedSemesterInfo,
  );

  // Invite/registration links only make sense for a semester still open for
  // enrollment (ongoing or future). For an ended semester the whole invite card
  // is hidden — students must never be enrolled into a past semester.
  const canInviteForSelectedSemester = computed(() => {
    const semester = selectedSemester.value;
    if (!semester?.documentId || !semester.end_date) return false;
    return !hasSemesterEnded(semester);
  });

  /**
   * Where the SELECTED semester sits relative to today — the class view is the
   * same page whether the teacher is looking back at a finished term, at the one
   * running now, or at one that hasn't started.
   */
  const semesterState = computed<SemesterState>(() => {
    const semester = selectedSemester.value;
    if (!semester?.documentId) return null;
    if (isCurrentSemester(semester)) return 'current';
    if (isUpcomingSemester(semester)) return 'future';
    return 'past';
  });

  /**
   * Opt-in for assigning into a semester that hasn't started.
   *
   * Deliberately OFF on every visit and reset whenever the selected semester
   * changes: assigning work to a future term is the exception, so the teacher
   * re-confirms it each time rather than inheriting a switch they flipped days ago.
   */
  const allowFutureAssignments = ref(false);
  watch(
    () => selectedSemester.value?.documentId,
    () => {
      allowFutureAssignments.value = false;
    },
  );

  /**
   * Adding assignments is a present-tense action: allowed in the running semester,
   * never in a finished one, and in a future one only behind the toggle above.
   * REMOVING an assignment stays available in every semester — a teacher must be
   * able to undo a mistake in a term they can no longer add to.
   */
  const canAddAssignments = computed(() => {
    if (!canManageClassCourses.value) return false;
    if (semesterState.value === 'current') return true;
    return semesterState.value === 'future' && allowFutureAssignments.value;
  });

  /** Notifies why an add was refused; the UI disables these paths up front. */
  function rejectAssignmentAdd(): void {
    Notify.create({
      message:
        semesterState.value === 'past'
          ? t('SCHOOL_ASSIGN_LOCKED_PAST')
          : t('SCHOOL_ASSIGN_LOCKED_FUTURE'),
      color: 'warning',
    });
  }

  // The class roster, its enrollments, and student progress are all scoped to the
  // selected semester (the class itself is eternal; the roster is per-semester).
  const semesterScope = () =>
    schoolFiltersStore.selectedSemesterDocumentId || undefined;

  // The class-load banner is a genuine error only while a semester is selected.
  // With no semester the class is intentionally not loaded (the view shows a
  // "select a semester" prompt instead), so ACTION_FAILED would be misleading.
  const showClassLoadError = computed(
    () =>
      hasSelectedSemester.value &&
      !isClassDataLoading.value &&
      !classDetail.value,
  );

  const studentOptionItems = computed(() =>
    assignableStudentOptions(classDetail.value?.enrollments),
  );

  const courseOptionItems = computed(() =>
    assignedCourses.value.map((course) => ({
      value: course.documentId,
      label: course.course_name,
    })),
  );

  const sectionOptionItems = computed(() =>
    assignableSectionOptions(
      classDetail.value?.assigned_sections,
      assignedSections.value,
    ),
  );

  async function refreshAssignedCourses() {
    if (!classDocumentId.value || !hasSelectedSemester.value) {
      assignedCourses.value = [];
      totalAssignedCourses.value = 0;
      selectedCourseDocumentId.value = '';
      return;
    }

    const response = await fetchClassAssignedCourses(classDocumentId.value, {
      search: assignedCoursesSearch.value,
      limit: 12,
      semesterDocumentId: semesterScope(),
    });

    assignedCourses.value = response.courses;
    totalAssignedCourses.value = response.totalCourses;

    const hasSelectedCourse = assignedCourses.value.some(
      (course) => course.documentId === selectedCourseDocumentId.value,
    );

    if (!hasSelectedCourse) {
      selectedCourseDocumentId.value =
        assignedCourses.value[0]?.documentId || '';
    }
  }

  async function refreshAssignedSections() {
    if (!classDocumentId.value || !hasSelectedSemester.value) {
      assignedSections.value = [];
      totalAssignedSections.value = 0;
      selectedSectionDocumentId.value = '';
      return;
    }

    const response = await fetchClassAssignedSections(classDocumentId.value, {
      search: assignedSectionsSearch.value,
      limit: 12,
      semesterDocumentId: semesterScope(),
    });

    assignedSections.value = response.sections;
    totalAssignedSections.value = response.totalSections;

    const hasSelectedSection = assignedSections.value.some(
      (section) => section.documentId === selectedSectionDocumentId.value,
    );

    if (!hasSelectedSection) {
      selectedSectionDocumentId.value =
        assignedSections.value[0]?.documentId || '';
    }
  }

  async function loadClassDetailAndCourses(documentId: string) {
    // A semester must be selected to view a class. The school filter is optional
    // and does NOT gate viewing — a class is a specific entity regardless of the
    // "All schools"/specific-school selection.
    if (!documentId || !hasSelectedSemester.value) {
      classDetail.value = null;
      assignedCourses.value = [];
      assignedSections.value = [];
      totalAssignedCourses.value = 0;
      totalAssignedSections.value = 0;
      selectedCourseDocumentId.value = '';
      selectedSectionDocumentId.value = '';
      progressRows.value = [];
      sectionProgressRows.value = [];
      return;
    }

    isClassDataLoading.value = true;
    try {
      classDetail.value = await fetchClassDetail(documentId, semesterScope());

      if (!canManageClassCourses.value) return;

      await Promise.all([refreshAssignedCourses(), refreshAssignedSections()]);

      if (!selectedCourseDocumentId.value && assignedCourses.value.length) {
        selectedCourseDocumentId.value =
          assignedCourses.value[0]?.documentId || '';
      }
      if (!selectedSectionDocumentId.value && assignedSections.value.length) {
        selectedSectionDocumentId.value =
          assignedSections.value[0]?.documentId || '';
      }

      await Promise.all([
        loadClassCourseProgress(),
        loadClassSectionProgress(),
      ]);
    } finally {
      isClassDataLoading.value = false;
    }
  }

  async function loadClassCourseProgress() {
    if (!selectedCourseDocumentId.value || !classDocumentId.value) {
      progressRows.value = [];
      return;
    }

    isProgressLoading.value = true;
    try {
      const payload = await fetchClassCourseProgress(
        classDocumentId.value,
        selectedCourseDocumentId.value,
        semesterScope(),
      );
      progressRows.value = payload?.rows || [];
    } finally {
      isProgressLoading.value = false;
    }
  }

  async function onSelectAssignedCourse(courseDocumentId: string) {
    selectedCourseDocumentId.value = courseDocumentId;
    await loadClassCourseProgress();
  }

  async function loadClassSectionProgress() {
    if (!selectedSectionDocumentId.value || !classDocumentId.value) {
      sectionProgressRows.value = [];
      return;
    }

    isSectionProgressLoading.value = true;
    try {
      const payload = await fetchClassSectionProgress(
        classDocumentId.value,
        selectedSectionDocumentId.value,
        semesterScope(),
      );
      sectionProgressRows.value = payload?.rows || [];
    } finally {
      isSectionProgressLoading.value = false;
    }
  }

  async function onSelectAssignedSection(sectionDocumentId: string) {
    selectedSectionDocumentId.value = sectionDocumentId;
    await loadClassSectionProgress();
  }

  async function openAssignmentDialogFromCourse(courseDocumentId: string) {
    if (!courseDocumentId) return;

    await onSelectAssignedCourse(courseDocumentId);
    assignmentDialogTab.value = 'courses';
    assignmentDialogCourseIds.value = [courseDocumentId];
    assignmentDialogSectionIds.value = [];
    assignmentDialogStudentIds.value = progressRows.value
      .filter((row) => row.assigned && !!row.student?.documentId)
      .map((row) => row.student?.documentId || '');
    isAssignmentDialogOpen.value = true;
  }

  async function openAssignmentDialogFromStudent(studentDocumentId: string) {
    if (!studentDocumentId || !classDocumentId.value) return;

    assignmentDialogStudentIds.value = [studentDocumentId];
    assignmentDialogTab.value = 'courses';
    assignmentDialogSectionIds.value = [];

    try {
      if (!assignedCourses.value.length) {
        await refreshAssignedCourses();
      }
      if (!assignedSections.value.length) {
        await refreshAssignedSections();
      }

      const progressByCourse = await Promise.all(
        assignedCourses.value.map(async (course) => {
          const payload = await fetchClassCourseProgress(
            classDocumentId.value,
            course.documentId,
            semesterScope(),
          );
          const row = (payload?.rows || []).find(
            (item) => item.student?.documentId === studentDocumentId,
          );
          return {
            courseDocumentId: course.documentId,
            courseName: course.course_name,
            assigned: !!row?.assigned,
          };
        }),
      );

      assignmentDialogCourseIds.value = progressByCourse
        .filter((item) => item.assigned)
        .map((item) => item.courseDocumentId);

      const progressBySection = await Promise.all(
        assignedSections.value.map(async (section) => {
          const payload = await fetchClassSectionProgress(
            classDocumentId.value,
            section.documentId,
            semesterScope(),
          );
          const row = (payload?.rows || []).find(
            (item) => item.student?.documentId === studentDocumentId,
          );
          return {
            sectionDocumentId: section.documentId,
            assigned: !!row?.assigned,
          };
        }),
      );

      assignmentDialogSectionIds.value = progressBySection
        .filter((item) => item.assigned)
        .map((item) => item.sectionDocumentId);
      isAssignmentDialogOpen.value = true;
    } catch {
      Notify.create({
        message: t('ACTION_FAILED'),
        color: 'negative',
      });
    }
  }

  function openAssignmentDialogFromSection(sectionDocumentId: string) {
    if (!sectionDocumentId) return;
    assignmentDialogTab.value = 'sections';
    assignmentDialogSectionIds.value = [sectionDocumentId];
    assignmentDialogCourseIds.value = [];
    assignmentDialogStudentIds.value = sectionProgressRows.value
      .filter((row) => row.assigned && !!row.student?.documentId)
      .map((row) => row.student?.documentId || '');
    isAssignmentDialogOpen.value = true;
  }

  function openAssignmentDialogFromOverview() {
    if (activeTab.value === 'sections') {
      assignmentDialogTab.value = 'sections';
      assignmentDialogSectionIds.value = selectedSectionDocumentId.value
        ? [selectedSectionDocumentId.value]
        : [];
      assignmentDialogCourseIds.value = [];
    } else {
      assignmentDialogTab.value = 'courses';
      assignmentDialogCourseIds.value = selectedCourseDocumentId.value
        ? [selectedCourseDocumentId.value]
        : [];
      assignmentDialogSectionIds.value = [];
    }

    assignmentDialogStudentIds.value = [];
    isAssignmentDialogOpen.value = true;
  }

  async function applyAssignmentSelections(payload: {
    studentIds: string[];
    courseIds: string[];
    sectionIds: string[];
  }) {
    if (
      !classDocumentId.value ||
      !hasSelectedSemester.value ||
      !payload.studentIds.length ||
      (!payload.courseIds.length && !payload.sectionIds.length)
    ) {
      return;
    }

    // This dialog only ever assigns (bulk `assigned: true`), so it is closed
    // outright in a locked semester rather than filtered.
    if (!canAddAssignments.value) {
      rejectAssignmentAdd();
      return;
    }

    isApplyingAssignments.value = true;
    let changedCount = 0;
    let unchangedCount = 0;
    const courseCount = payload.courseIds.length;
    const sectionCount = payload.sectionIds.length;
    const studentCount = payload.studentIds.length;

    try {
      for (const courseDocumentId of payload.courseIds) {
        const result = await bulkSetStudentCourseAssignment(
          classDocumentId.value,
          courseDocumentId,
          payload.studentIds,
          true,
        );
        if (!result) continue;
        changedCount += result.changedCount || 0;
        unchangedCount += result.unchangedCount || 0;
      }

      for (const sectionDocumentId of payload.sectionIds) {
        const result = await bulkSetStudentSectionAssignment(
          classDocumentId.value,
          sectionDocumentId,
          payload.studentIds,
          true,
        );
        if (!result) continue;
        changedCount += result.changedCount || 0;
        unchangedCount += result.unchangedCount || 0;
      }

      Notify.create({
        message: t('SCHOOL_BULK_ASSIGNMENT_SUMMARY', {
          courseCount,
          sectionCount,
          studentCount,
          changedCount,
          unchangedCount,
        }),
        color: 'positive',
      });

      await Promise.all([
        refreshAssignedCourses(),
        refreshAssignedSections(),
        loadClassCourseProgress(),
        loadClassSectionProgress(),
      ]);
      isAssignmentDialogOpen.value = false;
    } finally {
      isApplyingAssignments.value = false;
    }
  }

  async function updateCourseStudentAssignment(payload: {
    studentDocumentId: string;
    assigned: boolean;
  }) {
    if (!classDocumentId.value || !selectedCourseDocumentId.value) return;
    // Only the add direction is gated — unassigning stays open in any semester.
    if (payload.assigned && !canAddAssignments.value) {
      rejectAssignmentAdd();
      return;
    }

    const result = await setStudentCourseAssignment(
      classDocumentId.value,
      selectedCourseDocumentId.value,
      payload.studentDocumentId,
      payload.assigned,
    );

    if (!result) {
      Notify.create({
        message: t('ACTION_FAILED'),
        color: 'negative',
      });
      return;
    }

    await Promise.all([refreshAssignedCourses(), loadClassCourseProgress()]);
  }

  async function updateSectionStudentAssignment(payload: {
    studentDocumentId: string;
    assigned: boolean;
  }) {
    if (!classDocumentId.value || !selectedSectionDocumentId.value) return;
    // Only the add direction is gated — unassigning stays open in any semester.
    if (payload.assigned && !canAddAssignments.value) {
      rejectAssignmentAdd();
      return;
    }

    const result = await setStudentSectionAssignment(
      classDocumentId.value,
      selectedSectionDocumentId.value,
      payload.studentDocumentId,
      payload.assigned,
    );

    if (!result) {
      Notify.create({
        message: t('ACTION_FAILED'),
        color: 'negative',
      });
      return;
    }

    await Promise.all([refreshAssignedSections(), loadClassSectionProgress()]);
  }

  watch(
    () => classDocumentId.value,
    async (documentId) => {
      await loadClassDetailAndCourses(documentId);
    },
    { immediate: true },
  );

  // Reload the class when the filters change (e.g. the semester is auto-seeded on
  // a deep-linked class page, or the school filter changes). Navigation away on a
  // genuine semester switch is owned solely by MainDrawer's exit watcher, so this
  // stays a pure reload — no redirect, avoiding a double-watcher race.
  watch(
    () => [
      schoolFiltersStore.selectedSchoolDocumentId,
      schoolFiltersStore.selectedSemesterDocumentId,
    ],
    async () => {
      await loadClassDetailAndCourses(classDocumentId.value);
    },
  );

  watch(
    () => assignedCoursesSearch.value,
    async () => {
      if (!classDocumentId.value || !canManageClassCourses.value) return;
      await refreshAssignedCourses();
    },
  );

  watch(
    () => assignedSectionsSearch.value,
    async () => {
      if (!classDocumentId.value || !canManageClassCourses.value) return;
      await refreshAssignedSections();
    },
  );

  watch(
    () => visibleTabs.value,
    (tabs) => {
      if (!tabs.includes(activeTab.value)) {
        activeTab.value = tabs[0] || 'info';
      }
    },
    { immediate: true },
  );

  return {
    activeTab,
    visibleTabs,
    classDetail,
    assignedCourses,
    totalAssignedCourses,
    assignedCoursesSearch,
    isAssignedCoursesExpanded,
    selectedCourseDocumentId,
    selectedAssignedCourse,
    progressRows,
    assignedSections,
    totalAssignedSections,
    assignedSectionsSearch,
    isAssignedSectionsExpanded,
    selectedSectionDocumentId,
    selectedAssignedSection,
    sectionProgressRows,
    isClassDataLoading,
    showClassLoadError,
    hasSelectedSemester,
    selectedSemester,
    semesterState,
    allowFutureAssignments,
    canAddAssignments,
    canInviteForSelectedSemester,
    isProgressLoading,
    isSectionProgressLoading,
    canManageClassCourses,
    canDecide,
    handleDecision,
    isAssignmentDialogOpen,
    assignmentDialogTab,
    assignmentDialogStudentIds,
    assignmentDialogCourseIds,
    assignmentDialogSectionIds,
    studentOptionItems,
    courseOptionItems,
    sectionOptionItems,
    isApplyingAssignments,
    onSelectAssignedCourse,
    onSelectAssignedSection,
    openAssignmentDialogFromCourse,
    openAssignmentDialogFromStudent,
    openAssignmentDialogFromSection,
    openAssignmentDialogFromOverview,
    applyAssignmentSelections,
    updateCourseStudentAssignment,
    updateSectionStudentAssignment,
    isStudentStatsDialogOpen,
    statsStudentName,
    studentStatsSummary,
    studentStatsLoading,
    studentStatsError,
    openStudentStats,
  };
}
